<script setup lang="ts">
import { useUiStore } from '@/stores/ui'
import type { ToastSeverity } from '@/stores/ui'

const ui = useUiStore()

const severityClasses: Record<ToastSeverity, string> = {
  success: 'bg-success/10 border-success text-success',
  error: 'bg-danger/10 border-danger text-danger',
  warning: 'bg-warning/10 border-warning text-warning',
  info: 'bg-primary/10 border-primary text-primary',
}

const severityIcons: Record<ToastSeverity, string> = {
  success: 'pi pi-check-circle',
  error: 'pi pi-times-circle',
  warning: 'pi pi-exclamation-triangle',
  info: 'pi pi-info-circle',
}
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed top-4 right-4 z-toast flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
      data-testid="toast-container"
    >
      <TransitionGroup
        enter-active-class="transition-all duration-slow ease-out"
        enter-from-class="opacity-0 translate-x-8"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition-all duration-fast ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 translate-x-8"
      >
        <div
          v-for="toast in ui.toasts"
          :key="toast.id"
          :class="[
            'pointer-events-auto border-l-4 rounded-md shadow-toast p-4',
            severityClasses[toast.severity],
          ]"
          role="alert"
          data-testid="toast-item"
        >
          <div class="flex items-start gap-3">
            <i :class="[severityIcons[toast.severity], 'text-lg mt-0.5']" aria-hidden="true" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold" data-testid="toast-summary">
                {{ toast.summary }}
              </p>
              <p
                v-if="toast.detail"
                class="text-sm mt-1 opacity-90"
                data-testid="toast-detail"
              >
                {{ toast.detail }}
              </p>
            </div>
            <button
              class="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors duration-fast"
              aria-label="Dismiss notification"
              data-testid="toast-dismiss"
              @click="ui.removeToast(toast.id)"
            >
              <i class="pi pi-times text-sm" aria-hidden="true" />
            </button>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
